// Make it yours: the look, accent, mode, text size, sounds and detail. Drawn
// through the kit (LOOKS.md Part 7).
//
// A SHEET OVER THE SCREEN BEING LOOKED AT, not a page of its own (B-399,
// PLAN-2026-10-17 §2.2): every change applies at once to the screen behind,
// so a look is tried on the screen the person cares about, and closing it
// leaves that screen as it was. `shell/appearanceSheet.tsx` holds the sheet
// and the address; this is what is in it.
//
// Signed in, every choice here is the account's and follows the person to
// every device (M2, PLAN-2026-10-02 §2); the detail level and the text size
// can be kept just on this device instead (§3), which is why those two share
// a card with the box that says so. A visitor's choices stay in the browser.

import { useEffect, useMemo } from 'react';
import {
  DETAIL_LABEL,
  DETAIL_LEVELS,
  THEME_PRESETS,
  resolveTheme,
  themeToCssVars,
  type DetailLevel,
  type Theme,
} from '@fsp/theme';
import { getSport, lookupTeamPalette } from '../sports/registry';
import { useTheme } from '../theme/ThemeProvider';
import { useDelight } from '../delight/useDelight';
import { useAccountPrefs } from '../data/accountPrefs';
import { useDetailContext } from '../data/detail';
import { useSession } from '../data/session';
import { LookScope, loadLookFonts, useLookContext } from '../data/look';
import { LOOKS, lookOf } from '../looks/registry';
import { Icon } from '../kit/Icon';
import { Card, Caption, Checkbox, Choice, Colored, OneOf, Stack, Text, Toggle } from '../kit/ui';
import '../styles/screens/account.css';

/** The text sizes, as the control and the box's sentence both name them. */
const TEXT_SIZE: { value: Theme['fontScale']; label: string }[] = [
  { value: 0.9, label: 'Small' },
  { value: 1, label: 'Default' },
  { value: 1.1, label: 'Large' },
  { value: 1.2, label: 'Larger' },
];

const textSizeLabel = (scale: Theme['fontScale']) =>
  TEXT_SIZE.find((t) => t.value === scale)?.label ?? 'Default';

/** What each stop means, in the fewest words that are still true. */
const DETAIL_BLURB: Record<DetailLevel, string> = {
  simple: 'The fewest numbers that still answer the screen. Taller rows, more room around each.',
  standard: 'The numbers most managers look at, without the research columns.',
  pro: 'Every column there is, opened as a table. Denser rows.',
};

export function AppearanceBody() {
  const { theme, resolved, setTheme } = useTheme();
  const { play } = useDelight();
  const { detail, setDetail } = useDetailContext();
  const { session } = useSession();
  const { account, deviceOwn, setDeviceOwn } = useAccountPrefs();
  const { look } = useLookContext();
  const chosen = lookOf(look);
  const fixedMode = chosen.mode ? { label: chosen.label, mode: chosen.mode } : null;
  const football = getSport('football');
  // A theme saved while the Rams were keyed `LAR` names them by what is now
  // their abbreviation: picked as the club it names (`teams.ts`).
  const accent = theme.accent;
  const accentTeam =
    accent.kind === 'team'
      ? (football.proTeams.find((t) => t.id === accent.teamId) ??
        football.proTeams.find((t) => t.abbreviation === accent.teamId))
      : undefined;
  const accentValue =
    theme.accent.kind === 'team'
      ? `team:${accentTeam?.id ?? theme.accent.teamId}`
      : theme.accent.kind === 'preset'
        ? `preset:${theme.accent.id}`
        : 'custom';

  const options = [
    {
      group: 'Presets',
      items: THEME_PRESETS.map((p) => ({ value: `preset:${p.id}`, label: p.label })),
    },
    {
      group: `${football.proLeagueName} teams`,
      items: football.proTeams.map((t) => ({ value: `team:${t.id}`, label: t.name })),
    },
  ];

  const onAccent = (v: string | null) => {
    if (!v) return;
    const [kind, id] = v.split(':') as [string, string];
    const accent: Theme['accent'] =
      kind === 'team'
        ? { kind: 'team', sport: 'football', teamId: id }
        : { kind: 'preset', id: id as (typeof THEME_PRESETS)[number]['id'] };
    setTheme({ ...theme, accent });
  };

  return (
    <div className="fsp-appearance">
      <Stack gap={4}>
        <Text tone="secondary" block>
          {session === null
            ? 'Kept in this browser.'
            : 'Saved to your account, so every device you sign in on starts from it.'}
        </Text>
        <LookChoice />
        <div data-tour="appearance">
          <Card padded>
            <Stack gap={3}>
              <Choice
                label="Accent"
                options={options}
                value={accentValue === 'custom' ? null : accentValue}
                onChange={onAccent}
                required
              />
              <OneOf
                label="Mode"
                value={theme.mode}
                onChange={(mode) => setTheme({ ...theme, mode })}
                options={[
                  { value: 'dark', label: 'Dark' },
                  { value: 'light', label: 'Light' },
                  { value: 'system', label: 'System' },
                ]}
              />
              {/* A look drawn in one mode only says so here, where the Mode
                  that does nothing under it is chosen (B-207). */}
              {fixedMode !== null ? (
                <Text size="xs" tone="secondary" block>
                  {fixedMode.label} is always {fixedMode.mode === 'dark' ? 'dark' : 'light'}. Your
                  Mode comes back when you choose another look.
                </Text>
              ) : null}
              <Toggle
                label="Sounds"
                description="Score ticks, touchdowns, on-the-clock, and the win fanfare"
                on={theme.sounds}
                onChange={(on) => {
                  setTheme({ ...theme, sounds: on });
                  if (on) setTimeout(() => play('claimWon'), 50);
                }}
              />
              <Text size="xs" tone="secondary" block>
                Accent {resolved.tokens.accent} · text {resolved.tokens['accent-text']}
                {/* Which one it fell back to, since the resolver tries the
                    secondary and then the ink: saying "secondary" when it was
                    the ink was untrue on a light ground. Read from the
                    resolver, not by comparing colors: the words are lifted
                    until they read (B-277), so a lifted secondary is no
                    longer the secondary's exact value. */}
                {resolved.accentTextFrom === 'primary'
                  ? ''
                  : resolved.accentTextFrom === 'secondary'
                    ? ' (using your secondary color for text so it stays readable)'
                    : ' (using the plain text color for accent words so they stay readable)'}
              </Text>
            </Stack>
          </Card>
        </div>
        <div data-tour="pro-mode">
          <Card padded>
            <Stack gap={2}>
              <OneOf
                label="How much detail"
                value={detail}
                onChange={setDetail}
                // Built from the level list rather than typed out: a fourth
                // stop, or a relabelled one, must not be able to appear in
                // the app and miss this control.
                options={DETAIL_LEVELS.map((level) => ({
                  value: level,
                  label: DETAIL_LABEL[level],
                }))}
              />
              <Text size="xs" tone="secondary" block>
                {DETAIL_BLURB[detail]}
              </Text>
              <OneOf
                label="Text size"
                value={String(theme.fontScale)}
                onChange={(v) => setTheme({ ...theme, fontScale: Number(v) as Theme['fontScale'] })}
                options={TEXT_SIZE.map((t) => ({ value: String(t.value), label: t.label }))}
              />
              {/* Only once the account's two are known, because the
                    sentence names them (§3). */}
              {session !== null && account !== null ? (
                <Checkbox
                  label="Just on this device"
                  description={
                    deviceOwn
                      ? `Your other devices: ${DETAIL_LABEL[account.detail]}, ${textSizeLabel(account.theme.fontScale)} text. Untick to go back to them.`
                      : 'Off, these two are the same on every device you sign in on.'
                  }
                  on={deviceOwn}
                  onChange={setDeviceOwn}
                />
              ) : null}
            </Stack>
          </Card>
        </div>
        <Text tone="secondary" block>
          Custom colors and a team logo arrive with the settings screens.
        </Text>
      </Stack>
    </div>
  );
}

/**
 * The look (P9): each one drawn as a small sample in the person's own palette
 * and mode, so the choice is made by seeing it. Applied at once; the account
 * menu's Look item is the same choice without the pictures.
 */
function LookChoice() {
  const { theme, resolved } = useTheme();
  const { look, setLook } = useLookContext();
  // The samples are drawn in each look's own fonts, so a look's fonts load
  // when its sample is on screen, not only once it is chosen.
  useEffect(() => {
    for (const l of LOOKS) loadLookFonts(l.id);
  }, []);
  // Each sample wears the palette resolved on that look's ground, so Crayon's
  // shows paper while the page is still Clean.
  const vars = useMemo(
    () =>
      new Map(
        LOOKS.map((l) => [
          l.id,
          themeToCssVars(
            resolveTheme(
              { ...theme, mode: l.mode ?? theme.mode },
              {
                systemDark: resolved.mode === 'dark',
                lookupTeam: lookupTeamPalette,
                grounds: l.grounds,
              },
            ),
          ) as Record<`--${string}`, string>,
        ]),
      ),
    [theme, resolved.mode],
  );
  return (
    <Card padded>
      <Stack gap={2}>
        <Caption>Look</Caption>
        <div className="fsp-look-choices" role="radiogroup" aria-label="Look">
          {LOOKS.map(({ id, label, blurb }) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={look === id}
              className="fsp-choice fsp-look-choice"
              onClick={() => setLook(id)}
            >
              <LookScope look={id}>
                <Colored inline className="fsp-look-sample" data-look={id} colors={vars.get(id)!}>
                  <span className="fsp-disp fsp-look-sample-title">Week 3</span>
                  <span className="fsp-look-sample-row">
                    <Icon name="trophy" size="sm" />
                    <span className="fsp-grow">Your team</span>
                    <span className="fsp-look-sample-num fsp-look-sample-score">102.4</span>
                  </span>
                  <span className="fsp-look-sample-row fsp-look-sample-them">
                    <Icon name="people" size="sm" />
                    <span className="fsp-grow">Them</span>
                    <span className="fsp-look-sample-num">98.1</span>
                  </span>
                </Colored>
              </LookScope>
              <span className="fsp-choice-name">{label}</span>
              <span className="fsp-sub fsp-ui-wrap-text">{blurb}</span>
            </button>
          ))}
        </div>
        <Text size="xs" tone="secondary" block>
          A look changes how everything is drawn — type, lines, icons — and never what it says. Your
          colors and your detail level come with you.
        </Text>
      </Stack>
    </Card>
  );
}
